import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CidadeService } from '../../../services/configuracoes/cidade.service';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { TipoPessoa } from '../../../enums/tipoPessoa.enum';
import { TipoNaturezaFiscal } from '../../../enums/tipoNaturezaFiscal.enum';
import { Sexo } from '../../../enums/sexo.enum';
import { PessoaDTO, PessoaFormDTO } from '../../../models/clientes/pessoa.model';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject, Subscription, takeUntil } from 'rxjs';
import { PessoaRucResultado } from '../../../models/pessoas/pessoa-ruc-resultado.model';
import { PaisDDI } from '../../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../../shared/data/paises-ddi.data';

interface TipoPessoaOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-cliente',
  templateUrl: './cliente.component.html',
  styleUrls: ['./cliente.component.scss'],
  providers: [ConfirmationService, MessageService],
})
export class ClienteComponent implements OnInit, OnDestroy {
  form: PessoaFormDTO = this.createEmptyForm();
  cidadeList: { label: string; value: string; estado: string }[] = [];
  estadoCidade!: string;

  tipoPessoaOptions: { value: TipoPessoa; label: string }[] = [];
  tipoNaturezaFiscalOptions: { value: TipoNaturezaFiscal; label: string }[] = [];
  sexoOptions: { value: Sexo; label: string }[] = [];

  // DDI dropdown
  paisSelecionado: PaisDDI = PAISES_DDI[0];

  // Busca automatica por CI
  private ciInput$ = new Subject<string>();
  private ciDestroy$ = new Subject<void>();

  // Dialog pessoa ja cadastrada
  pessoaJaCadastradaDialogVisible = false;
  pessoaJaCadastradaMensagem = '';
  pessoaJaCadastradaId: number | null = null;

  // Textos traduzidos
  texts = {
    titulo: 'Adicionar Novo Cliente',
    subtitulo: 'Preencha os dados do novo cliente',
    identificacao: 'Identificação',
    contato: 'Contato',
    dadosPessoais: 'Dados Pessoais',
    endereco: 'Endereço'
  };

  labels = {
    ruc: 'RUC',
    ci: 'CI',
    nome: 'Nome',
    nomeFantasia: 'Nome Fantasia',
    tipoPessoa: 'Tipo Pessoa',
    tipoNaturezaFiscal: 'Tipo Natureza Fiscal',
    categoria: 'Categoria',
    cliente: 'Cliente',
    provedor: 'Provedor',
    ddi: 'DDI',
    telefone: 'Telefone',
    email: 'E-mail',
    dataNascimento: 'Data de Nascimento',
    sexo: 'Sexo',
    cidade: 'Cidade',
    estado: 'Estado',
    endereco: 'Endereço',
    numero: 'Número'
  };

  placeholders = {
    ruc: 'RUC do cliente',
    ci: 'CI',
    nome: 'Nome do cliente',
    nomeFantasia: 'Nome Fantasia',
    tipoPessoa: '-- Selecione o tipo de pessoa --',
    tipoNaturezaFiscal: '-- Selecione o tipo de natureza fiscal --',
    ddi: 'DDI',
    telefone: 'Número de telefone',
    email: 'E-mail do cliente',
    sexo: '-- Selecione o sexo --',
    cidade: '-- Selecione a cidade --',
    endereco: 'Endereço do cliente',
    numero: 'Número'
  };

  botoes = {
    voltar: 'Voltar',
    enviar: 'Enviar'
  };

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private cidadeService: CidadeService,
    private clienteService: PessoaService,
    private router: Router
  ) { }

  private langChangeSub!: Subscription;

  ngOnInit() {
    this.findCidades();
    this.loadTranslations();
    this.buildTipoPessoaOptions();
    this.buildTipoNaturezaFiscalOptions();
    this.buildSexoOptions();
    this.setDefaultDropdownValues();
    this.inicializarDebounceCI();

    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.loadTranslations();
      this.buildTipoPessoaOptions();
      this.buildTipoNaturezaFiscalOptions();
      this.buildSexoOptions();
      this.setDefaultDropdownValues();
    });
  }

  private loadTranslations() {
    this.texts = {
      titulo: this.translate.instant('add-cliente.texts.cadastro_de_cliente'),
      subtitulo: this.translate.instant('add-cliente.texts.subtitulo'),
      identificacao: this.translate.instant('add-cliente.texts.identificacao'),
      contato: this.translate.instant('add-cliente.texts.contato'),
      dadosPessoais: this.translate.instant('add-cliente.texts.dados_pessoais'),
      endereco: this.translate.instant('add-cliente.texts.endereco')
    };

    this.labels = {
      ruc: this.translate.instant('add-cliente.labels.ruc'),
      ci: this.translate.instant('add-cliente.labels.ci'),
      nome: this.translate.instant('add-cliente.labels.nome'),
      nomeFantasia: this.translate.instant('add-cliente.labels.nome_fantasia'),
      tipoPessoa: this.translate.instant('add-cliente.labels.tipo_pessoa'),
      tipoNaturezaFiscal: this.translate.instant('add-cliente.labels.tipo_natureza_fiscal'),
      categoria: this.translate.instant('add-cliente.labels.categoria'),
      cliente: this.translate.instant('add-cliente.labels.cliente'),
      provedor: this.translate.instant('add-cliente.labels.provedor'),
      ddi: this.translate.instant('add-cliente.labels.ddi'),
      telefone: this.translate.instant('add-cliente.labels.telefone'),
      email: this.translate.instant('add-cliente.labels.e_mail'),
      dataNascimento: this.translate.instant('add-cliente.labels.data_de_nascimento'),
      sexo: this.translate.instant('add-cliente.labels.sexo'),
      cidade: this.translate.instant('add-cliente.labels.cidade'),
      estado: this.translate.instant('add-cliente.labels.estado'),
      endereco: this.translate.instant('add-cliente.labels.endereco'),
      numero: this.translate.instant('add-cliente.labels.numero')
    };

    this.placeholders = {
      ruc: this.translate.instant('add-cliente.placeholders.ruc_do_cliente'),
      ci: this.translate.instant('add-cliente.placeholders.ci'),
      nome: this.translate.instant('add-cliente.placeholders.nome_do_cliente'),
      nomeFantasia: this.translate.instant('add-cliente.placeholders.nome_fantasia'),
      tipoPessoa: this.translate.instant('add-cliente.placeholders.selecione_o_tipo_de_pessoa'),
      tipoNaturezaFiscal: this.translate.instant('add-cliente.placeholders.selecione_o_tipo_de_natureza_fiscal'),
      ddi: this.translate.instant('add-cliente.placeholders.ddi'),
      telefone: this.translate.instant('add-cliente.placeholders.telefone'),
      email: this.translate.instant('add-cliente.placeholders.e_mail_do_cliente'),
      sexo: this.translate.instant('add-cliente.placeholders.selecione_o_sexo'),
      cidade: this.translate.instant('add-cliente.placeholders.selecione_a_cidade'),
      endereco: this.translate.instant('add-cliente.placeholders.endereco_do_cliente'),
      numero: this.translate.instant('add-cliente.placeholders.numero_do_endereco')
    };

    this.botoes = {
      voltar: this.translate.instant('add-cliente.botoes.voltar'),
      enviar: this.translate.instant('add-cliente.botoes.enviar')
    };
  }

  ngOnDestroy() {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.ciDestroy$.next();
    this.ciDestroy$.complete();
  }

  private buildTipoPessoaOptions() {
    this.tipoPessoaOptions = Object.values(TipoPessoa).map(tipo => ({
      value: tipo,
      label: this.translate.instant(`enums.tipo_pessoa.${tipo.toLowerCase()}`)
    }));
  }

  private buildTipoNaturezaFiscalOptions() {
    this.tipoNaturezaFiscalOptions = Object.values(TipoNaturezaFiscal).map(natureza => ({
      value: natureza,
      label: this.translate.instant(`enums.tipo_natureza_fiscal.${natureza.toLowerCase()}`)
    }));
  }

  private buildSexoOptions() {
    this.sexoOptions = Object.values(Sexo).map(sexo => ({
      value: sexo,
      label: this.translate.instant(`enums.sexo.${sexo.toLowerCase()}`)
    }));
  }

  findCidades(): Promise<void> {
    return new Promise((resolve) => {
      this.cidadeService.get('/cidade?listarTodos=true').subscribe((response) => {
        this.cidadeList = response.content.map((cidade: any) => ({
          label: cidade.nome,
          value: String(cidade.id),
          estado: cidade.nomeEstado
        }))
        resolve();
      });
    });
  }

  onSelecionarCidade(event: any) {
    // O showClear do p-dropdown emite onChange com value null: sem cidade, o estado tambem some.
    const valorCidade = event?.value?.value;
    if (valorCidade == null) {
      this.estadoCidade = '';
      return;
    }
    const cidadeSelecionada = this.cidadeList.find(cidade => cidade.value === valorCidade);
    if (cidadeSelecionada) {
      this.estadoCidade = cidadeSelecionada.estado;
    }
  }

  getDropItems<T extends object>(enumType: T): { label: string; value: T[keyof T] }[] {
    return Object.values(enumType).map((item) => ({
      label: String(item),
      value: item as T[keyof T],
    }));
  }

  applyMask(event: any) {
    let value = event.target.value.replace(/\D/g, ''); // Remove todos os caracteres não numéricos

    if (value.length > 12) {
      value = value.substring(0, 12);
    }

    if (value.length > 6) {
      value = value.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2})$/, '($1) $2-$3$4');
    } else if (value.length > 3) {
      value = value.replace(/^(\d{3})(\d{0,3})$/, '($1) $2');
    } else if (value.length > 0) {
      value = value.replace(/^(\d{0,3})$/, '($1');
    } else {
      value = '';
    }

    event.target.value = value;
    value = value.replace(/\s/g, ''); // Remove todos os espaços em branco
    this.form.telefone = value;
  }

  isFormSubmitted = false;
  submitting = false;

  validarCI(): boolean {
    const ci = (this.form.ci || '').trim();
    if (!ci) return true; // CI é opcional
    if (!/^\d+$/.test(ci)) return false; // Apenas dígitos
    return ci.length >= 5 && ci.length <= 8;
  }

  validarDataNascimento(): boolean {
    if (!this.form.dataNascimento) {
      return true;
    }
    // Verificar se a máscara foi totalmente preenchida (dd/MM/yyyy = 10 chars)
    return this.form.dataNascimento.length === 10;
  }

  submit() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (!this.form.tipoPessoa || !this.form.tipoNaturezaFiscal || !this.form.sexo || !this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('add-cliente.mensagens.campos_obrigatorios'),
      });
      return;
    }

    if (!this.validarCI()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('add-cliente.validacoes.ci_invalido'),
      });
      return;
    }

    if (!this.validarDataNascimento()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('erro'),
        detail: this.translate.instant('add-cliente.validacoes.data_nascimento_invalida'),
      });
      return;
    }

    // Limpar telefone: salvar apenas digitos puros (sem mascara)
    const telefoneRaw = this.form.telefone?.replace(/\D/g, '') || undefined;

    // dataNascimento já vem no formato dd/MM/yyyy do p-inputMask
    const dataNascimentoFormatada = this.form.dataNascimento || undefined;

    const body: PessoaDTO = {
      nome: this.form.nome,
      fantasia: this.form.fantasia || undefined,
      ruc: this.form.ruc || undefined,
      ci: this.form.ci || undefined,
      email: this.form.email || undefined,
      ddi: this.paisSelecionado.ddi.replace('+', '') || '595',
      telefone: telefoneRaw,
      endereco: this.form.endereco || undefined,
      numero: this.form.numero || undefined,
      cidade_id: this.form.cidade?.value ? Number(this.form.cidade.value) : undefined,
      categoriaCliente: true,
      categoriaFornecedor: false,
      categoriaVendedor: false,
      tipoPessoa: this.form.tipoPessoa.value,
      tipoNaturezaFiscal: this.form.tipoNaturezaFiscal.value,
      dataNascimento: dataNascimentoFormatada,
      sexo: this.form.sexo.value,
    };

    console.log('[DEBUG] Body enviado:', JSON.stringify(body, null, 2));
    this.submitting = true;
    this.clienteService.post('/pessoas', body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('add-cliente.mensagens.cliente_cadastrado'),
        });
        this.form = this.createEmptyForm();
        this.setDefaultDropdownValues();
        this.paisSelecionado = PAISES_DDI[0];
        this.findCidades();
        this.router.navigate(['lista-de-clientes']);
      },
      error: () => {
        this.submitting = false;
      },
    });
    this.isFormSubmitted = false;
  }



  private setDefaultDropdownValues() {
    // Tipo Pessoa: JURIDICA (índice 1)
    const tipoPessoaJuridica = this.tipoPessoaOptions.find(o => o.value === TipoPessoa.JURIDICA);
    if (tipoPessoaJuridica && !this.form.tipoPessoa) {
      this.form.tipoPessoa = tipoPessoaJuridica;
    }

    // Tipo Natureza Fiscal: CONTRIBUINTE (índice 0)
    const contribuinte = this.tipoNaturezaFiscalOptions.find(o => o.value === TipoNaturezaFiscal.CONTRIBUINTE);
    if (contribuinte && !this.form.tipoNaturezaFiscal) {
      this.form.tipoNaturezaFiscal = contribuinte;
    }

    // Sexo: MASCULINO (índice 0)
    const masculino = this.sexoOptions.find(o => o.value === Sexo.MASCULINO);
    if (masculino && !this.form.sexo) {
      this.form.sexo = masculino;
    }
  }

  createEmptyForm(): PessoaFormDTO {
    return {
      nome: '',
      fantasia: undefined,
      ruc: '',
      ci: '',
      email: '',
      ddi: '',
      telefone: '',
      endereco: '',
      numero: '',
      cidade: undefined,
      categoriaCliente: true,
      categoriaFornecedor: false,
      categoriaVendedor: false,
      tipoPessoa: undefined,
      tipoNaturezaFiscal: undefined,
      dataNascimento: undefined,
      sexo: undefined,
    };
  }

  onNomeBlur(): void {
    if (this.form.nome && !this.form.fantasia) {
      this.form.fantasia = this.form.nome;
    }
  }

  onTipoPessoaChange(): void {
    if (this.form.tipoPessoa?.value === TipoPessoa.FISICA) {
      this.form.ruc = '';
    } else if (this.form.tipoPessoa?.value === TipoPessoa.JURIDICA) {
      this.form.ci = '';
    }
  }

  isPessoaFisica(): boolean {
    return this.form.tipoPessoa?.value === TipoPessoa.FISICA;
  }

  onRucPessoaConfirmada(resultado: PessoaRucResultado): void {
    this.form.ruc = resultado.ruc || '';
    if (resultado.nome) this.form.nome = resultado.nome;
    if (resultado.nome && !this.form.fantasia) this.form.fantasia = resultado.nome;
    if (resultado.ddi) {
      const ddiComPlus = resultado.ddi.startsWith('+') ? resultado.ddi : `+${resultado.ddi}`;
      const paisEncontrado = PAISES_DDI.find(p => p.ddi === ddiComPlus);
      if (paisEncontrado) this.paisSelecionado = paisEncontrado;
    }
    if (resultado.telefone) this.form.telefone = resultado.telefone;
    if (resultado.cidade?.id) {
      const cidadeEncontrada = this.cidadeList.find(c => c.value === String(resultado.cidade!.id));
      if (cidadeEncontrada) {
        this.form.cidade = { label: cidadeEncontrada.label, value: Number(cidadeEncontrada.value) };
        this.estadoCidade = cidadeEncontrada.estado;
      }
    }
  }

  onRucPessoaVinculada(pessoa: PessoaDTO): void {
    this.pessoaJaCadastradaId = pessoa.id ?? null;
    this.pessoaJaCadastradaMensagem = this.translate.instant('add-cliente.mensagens.pessoa_ja_cadastrada', { nome: pessoa.nome });
    this.pessoaJaCadastradaDialogVisible = true;
  }

  onRucLimpo(): void {
    this.form.nome = '';
    this.form.fantasia = undefined;
    this.form.telefone = '';
    this.form.cidade = undefined;
    this.estadoCidade = '';
    this.paisSelecionado = PAISES_DDI[0];
  }

  onCiModelChange(value: string): void {
    const apenasDigitos = (value || '').replace(/\D/g, '').substring(0, 8);
    if (value !== apenasDigitos) {
      this.form.ci = apenasDigitos;
    }
  }

  onCiInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const apenasDigitos = (input.value || '').replace(/\D/g, '').substring(0, 8);
    if (input.value !== apenasDigitos) {
      input.value = apenasDigitos;
      this.form.ci = apenasDigitos;
    }
    const valor = (this.form.ci || '').trim();
    if (!valor || valor.length < 3) return;
    this.ciInput$.next(valor);
  }

  private inicializarDebounceCI(): void {
    this.ciInput$.pipe(
      debounceTime(1500),
      takeUntil(this.ciDestroy$),
    ).subscribe(ci => this.buscarPorCI(ci));
  }

  private buscarPorCI(ci: string): void {
    if (!ci || !this.form.ci) return;
    this.clienteService.get(`/pessoas/buscar?ci=${ci}&categoriaCliente=true&size=1`).subscribe({
      next: (res: any) => {
        const resultados = res?.content || [];
        if (resultados.length > 0) {
          const pessoa = resultados[0];
          this.pessoaJaCadastradaId = pessoa.id ?? null;
          this.pessoaJaCadastradaMensagem = this.translate.instant('add-cliente.mensagens.pessoa_ja_cadastrada', { nome: pessoa.nome });
          this.pessoaJaCadastradaDialogVisible = true;
        }
      },
    });
  }

  editarPessoaJaCadastrada(): void {
    this.pessoaJaCadastradaDialogVisible = false;
    if (this.pessoaJaCadastradaId) {
      this.router.navigate(['/clientes'], { queryParams: { edit: this.pessoaJaCadastradaId } });
    }
  }

  navigateListaDeClientes() {
    this.router.navigate(['lista-de-clientes']);
  }
}
